import { useState, useEffect, lazy, Suspense } from "react";
import { Routes, Route, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Stats from "./components/Stats";
import WhySection from "./components/WhySection";
import Features from "./components/Features";
import TalentProducts from "./components/TalentProducts";
import HowItWorks from "./components/HowItWorks";
import Competencies from "./components/Competencies";
import Pricing from "./components/Pricing";
import CTA from "./components/CTA";
import Footer from "./components/Footer";
import ContactModal from "./components/ContactModal";
// import CookieConsent from './components/CookieConsent';
import ScrollToTop from "./components/ScrollToTop";
import { routes, getRouteMatchFromPath } from "./utils/routes";

const PremiumInterview = lazy(() => import("./pages/PremiumInterview"));
const StandardInterview = lazy(() => import("./pages/StandardInterview"));
const VoiceInterview = lazy(() => import("./pages/VoiceInterview"));
const TextInterview = lazy(() => import("./pages/TextInterview"));
const WhatsAppInterview = lazy(() => import("./pages/WhatsAppInterview"));
const PhoneInterview = lazy(() => import("./pages/PhoneInterview"));
const InterviewObserver = lazy(() => import("./pages/InterviewObserver"));
const PeerInterview = lazy(() => import("./pages/PeerInterview"));
const CVScoring = lazy(() => import("./pages/CVScoring"));
const Blog = lazy(() => import("./pages/Blog"));
const Career = lazy(() => import("./pages/Career"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ATS = lazy(() => import("./pages/ATS"));
const Education = lazy(() => import("./pages/Education"));
const Assessments = lazy(() => import("./pages/Assessments"));
const KVKK = lazy(() => import("./pages/KVKK"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Newsletter = lazy(() => import("./pages/Newsletter"));
const ApplyCv = lazy(() => import("./pages/ApplyCv"));
const PeerEmployee = lazy(() => import("./pages/PeerEmployee"));
const PeerCandidate = lazy(() => import("./pages/PeerCandidate"));
const CvBuilder = lazy(() => import("./pages/CvBuilder"));

function HomePage({ audience, setAudience, openContact }) {
  return (
    <>
      <Hero
        audience={audience}
        onAudienceChange={setAudience}
        onContactClick={openContact}
      />
      {audience === "talent" ? (
        <>
          <TalentProducts />
          <CTA onContactClick={openContact} />
        </>
      ) : (
        <>
          <Stats />
          <WhySection />
          <Features audience={audience} />
          <HowItWorks />
          <Competencies />
          <Pricing onContactClick={openContact} />
          <CTA onContactClick={openContact} />
        </>
      )}
    </>
  );
}

function App() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // URL'den audience parametresini oku - başlangıçta URL'den bağımsız default değer
  // Bu, server/client arasında tutarlılık sağlar
  const [audience, setAudience] = useState("hiring");

  // URL'den audience değiştiğinde state'i güncelle
  useEffect(() => {
    const urlAudience = searchParams.get("audience");
    const newAudience = urlAudience === "talent" ? "talent" : "hiring";
    setAudience(newAudience);
  }, [searchParams]);

  // Dil değişikliği - sadece client-side hydration sonrası ve URL değişikliğinde
  useEffect(() => {
    const routeMatch = getRouteMatchFromPath(location.pathname);
    const isEnglishHome =
      location.pathname === "/en" || location.pathname.endsWith("/en");
    const targetLang =
      isEnglishHome || routeMatch?.language === "en" ? "en" : "tr";

    // Sadece dil değiştiğinde güncelle, sonsuz döngüyü önle
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  }, [location, i18n]);

  // Audience değiştiğinde URL'yi güncelle
  const handleAudienceChange = (newAudience) => {
    setAudience(newAudience);
    if (newAudience === "talent") {
      setSearchParams({ audience: "talent" }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const openContact = () => setIsContactOpen(true);
  const closeContact = () => setIsContactOpen(false);

  // Standalone pages (no Navbar/Footer) — for iframe/subdomain usage
  const isStandalone = [routes.tr.applyCv, routes.en.applyCv, routes.tr.peerEmployee, routes.en.peerEmployee, routes.tr.peerCandidate, routes.en.peerCandidate, routes.tr.cvBuilder, routes.en.cvBuilder].includes(location.pathname);

  return (
    <div className="min-h-screen bg-white">
      <ScrollToTop />
      {!isStandalone && <Navbar audience={audience} onContactClick={openContact} />}
      <main>
        <Suspense fallback={null}>
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                audience={audience}
                setAudience={handleAudienceChange}
                openContact={openContact}
              />
            }
          />
          <Route
            path="/en"
            element={
              <HomePage
                audience={audience}
                setAudience={handleAudienceChange}
                openContact={openContact}
              />
            }
          />
          {/* Turkish Routes */}
          <Route
            path={routes.tr.premiumInterview}
            element={<PremiumInterview />}
          />
          <Route
            path={routes.tr.standardInterview}
            element={<StandardInterview />}
          />
          <Route path={routes.tr.voiceInterview} element={<VoiceInterview />} />
          <Route path={routes.tr.textInterview} element={<TextInterview />} />
          <Route path={routes.tr.whatsappInterview} element={<WhatsAppInterview />} />
          <Route path={routes.tr.phoneInterview} element={<PhoneInterview />} />
          <Route path={routes.tr.interviewObserver} element={<InterviewObserver />} />
          <Route path={routes.tr.peerInterview} element={<PeerInterview />} />
          <Route path={routes.tr.cvScoring} element={<CVScoring />} />
          <Route path={routes.tr.assessments} element={<Assessments />} />
          <Route path={routes.tr.ats} element={<ATS />} />
          <Route path={routes.tr.training} element={<Education />} />
          <Route path={routes.tr.kvkk} element={<KVKK />} />
          <Route path={routes.tr.terms} element={<Terms />} />
          <Route path={routes.tr.privacy} element={<Privacy />} />
          <Route path={routes.tr.cookies} element={<Cookies />} />
          <Route path={routes.tr.blog} element={<Blog />} />
          <Route path={routes.tr.blogDetail} element={<Blog />} />
          <Route path={routes.tr.career} element={<Career />} />
          <Route path={routes.tr.jobDetail} element={<JobDetail />} />
          <Route path={routes.tr.contact} element={<Contact />} />
          <Route path={routes.tr.faq} element={<FAQ />} />
          <Route path={routes.tr.calendar} element={<Calendar />} />
          <Route path={routes.tr.newsletter} element={<Newsletter />} />
          <Route path={routes.tr.applyCv} element={<ApplyCv />} />
          <Route path={routes.tr.peerEmployee} element={<PeerEmployee />} />
          <Route path={routes.tr.peerCandidate} element={<PeerCandidate />} />
          <Route path={routes.tr.cvBuilder} element={<CvBuilder />} />
          {/* English Routes */}
          <Route
            path={routes.en.premiumInterview}
            element={<PremiumInterview />}
          />
          <Route
            path={routes.en.standardInterview}
            element={<StandardInterview />}
          />
          <Route path={routes.en.voiceInterview} element={<VoiceInterview />} />
          <Route path={routes.en.textInterview} element={<TextInterview />} />
          <Route path={routes.en.whatsappInterview} element={<WhatsAppInterview />} />
          <Route path={routes.en.phoneInterview} element={<PhoneInterview />} />
          <Route path={routes.en.interviewObserver} element={<InterviewObserver />} />
          <Route path={routes.en.peerInterview} element={<PeerInterview />} />
          <Route path={routes.en.cvScoring} element={<CVScoring />} />
          <Route path={routes.en.assessments} element={<Assessments />} />
          <Route path={routes.en.ats} element={<ATS />} />
          <Route path={routes.en.training} element={<Education />} />
          <Route path={routes.en.kvkk} element={<KVKK />} />
          <Route path={routes.en.terms} element={<Terms />} />
          <Route path={routes.en.privacy} element={<Privacy />} />
          <Route path={routes.en.cookies} element={<Cookies />} />
          <Route path={routes.en.blog} element={<Blog />} />
          <Route path={routes.en.blogDetail} element={<Blog />} />
          <Route path={routes.en.career} element={<Career />} />
          <Route path={routes.en.jobDetail} element={<JobDetail />} />
          <Route path={routes.en.contact} element={<Contact />} />
          <Route path={routes.en.faq} element={<FAQ />} />
          <Route path={routes.en.calendar} element={<Calendar />} />
          <Route path={routes.en.newsletter} element={<Newsletter />} />
          <Route path={routes.en.applyCv} element={<ApplyCv />} />
          <Route path={routes.en.peerEmployee} element={<PeerEmployee />} />
          <Route path={routes.en.peerCandidate} element={<PeerCandidate />} />
          <Route path={routes.en.cvBuilder} element={<CvBuilder />} />
        </Routes>
        </Suspense>
      </main>
      {!isStandalone && <Footer audience={audience} />}
      {!isStandalone && <ContactModal isOpen={isContactOpen} onClose={closeContact} />}
      {/* <CookieConsent /> */}
    </div>
  );
}

export default App;
